import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createMutation } from '../lib/mutation';
import type { RepoDetailRow } from '../lib/repo-detail';
import { setRepoDonationsEnabled } from '../lib/repos';
import { canOwnerWrite } from '../lib/roles';
import { useMe } from '../lib/session';
import { DonateBadgeSnippet } from './DonateBadgeSnippet';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';
import { Button } from './ui/Button';

// Repo Settings → "accept donations" (arc 101 slice 11, plan doc 05) — the
// SECOND real write plane on this tab, and real is the whole point: #966 pulled
// the fake switches off this surface, so a control here either writes or renders
// read-only. This one writes. `donations_enabled` is its own column (slice 02),
// flipped by `PATCH /v1/repos/:owner/:repo`, deliberately NOT a `.maintainer.yml`
// key — who may fund the repo is an ownership/billing fact, and the yml is
// in-tree, so any PR author could otherwise propose it.
//
// Unlike its neighbour it does NOT navigate away. Disabling a repo hides it from
// every list read, which is why enablement lands the reader on the grid; a
// donations flip changes nothing about where this workspace lives. So it re-READS
// instead — `onSaved` refetches the repo row and the pill states what is STORED,
// never what was last sent.
//
// It re-derives nothing. Donatable is `enabled AND NOT private AND
// donations_enabled`, settled in ONE server-side predicate; the projection
// carries neither of the other two conjuncts, and a second copy of that rule
// here is exactly the drift the single predicate exists to prevent. The copy
// therefore says where the switch takes effect rather than pretending to know:
// a repo that goes private keeps its opt-in PARKED, and gets it back when it
// goes public again.

export function RepoDonationsPanel(props: {
  readonly repo: RepoDetailRow;
  readonly token: string | null;
  /** Re-read the repo row after a flip — the panel never patches from the echo. */
  readonly onSaved: () => void;
}): JSX.Element {
  const me = useMe(props.token);

  // `write:repos` mirror, same shape as `<RepoEnableSwitchPanel/>`: shut while
  // `/v1/me` is in flight (mid-load `role` is undefined, the same shape as the
  // PAT session that KEEPS the control), and `denied` is not `!allowed` —
  // mid-load the write is unknown, not refused.
  const allowed = (): boolean => !me.isLoading && canOwnerWrite(me.data?.role);
  const denied = (): boolean => !me.isLoading && !canOwnerWrite(me.data?.role);
  const on = (): boolean => props.repo.donations_enabled === true;

  const toggle = createMutation({
    call: () => setRepoDonationsEnabled(props.repo.full_name, !on(), props.token),
    fallback: t('repo_detail.donations_failed'),
    onSuccess: () => props.onSaved(),
  });
  const busy = toggle.pending;
  const error = toggle.error;
  const flip = (): void => void toggle.run();

  return (
    <Panel
      title={t('repo_detail.donations_title')}
      action={
        <Button variant="secondary" size="sm" disabled={busy() || !allowed()} onClick={flip}>
          {on() ? t('repo_detail.donations_stop') : t('repo_detail.donations_start')}
        </Button>
      }
    >
      <div class="repo-settings__enablement">
        <StatusPill tone={on() ? 'success' : 'neutral'}>
          {on() ? t('repo_detail.donations_on') : t('repo_detail.donations_off')}
        </StatusPill>
        <p class="repo-settings__note">{t('repo_detail.donations_desc')}</p>
      </div>
      {/* The badge rides the opt-in: the marketing endpoint answers a repo nobody
          may fund with the STATIC pill, so handing over the snippet while the
          switch is off would put a dead badge in someone's README. */}
      <Show when={on()}>
        <DonateBadgeSnippet slug={props.repo.full_name} class="repo-settings__badge" />
      </Show>
      <Show when={error()}>
        {(reason) => (
          <p class="repo-settings__note" role="alert">
            {reason()}
          </p>
        )}
      </Show>
      {/* A dead button with no reason reads as a bug; say why it's dead. */}
      <Show when={denied()}>
        <p class="repo-settings__note" role="status">
          {t('repo_detail.donations_denied')}
        </p>
      </Show>
    </Panel>
  );
}
